import React from "react";
import { useTranslation } from "react-i18next";

import { ResourceHubPage } from "turboui";
import {
  useAddFileWidgetProps,
  useNewFileModalsContextValue,
  useResourceHubNodesListProps,
  useCreateFolder,
  useRenameCollection,
} from "@/models/resourceHubs";
import { useResourceHubSearchProps } from "@/models/search/resourceHub";

import { usePaths } from "@/routes/paths";
import { useLoadedData, useRefresh } from "./loader";
import { buildResourceHubPageNavigation } from "./navigation";
import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";

export function Page() {
  const { t } = useTranslation();
  const { resourceHub, nodes, draftNodes } = useLoadedData();
  const refresh = useRefresh();
  const paths = usePaths();
  const search = useResourceHubSearchProps(resourceHub.id);
  const formattedTimePreferences = useFormattedTimePreferences();

  const newFileModalsContext = useNewFileModalsContextValue({ resourceHub });
  const addFileWidgetProps = useAddFileWidgetProps({ resourceHub, onUploaded: refresh });
  const { mutateAsync: createFolder } = useCreateFolder();
  const { mutateAsync: renameCollection } = useRenameCollection({
    resourceHubId: resourceHub.id,
    spaceId: resourceHub.space?.id,
  });
  const nodesListProps = useResourceHubNodesListProps({ resourceHub, type: "resource_hub", nodes, refetch: refresh });

  const props: ResourceHubPage.Props = {
    title: resourceHub.name || t("Docs & Files"),
    navigation: buildResourceHubPageNavigation(resourceHub, paths),
    resourceHub,
    renameCollection: async (resourceHubId, newName) => {
      await renameCollection({ resourceHubId, newName });
    },
    drafts: {
      nodes: draftNodes,
      draftsPath: paths.resourceHubDraftsPath(resourceHub.id),
    },
    newFileModals: newFileModalsContext,
    addFileWidgetProps,
    nodesListProps,
    search,
    formattedTimePreferences,
    addFolderModalProps: {
      resourceHubId: resourceHub.id,
      onCreated: refresh,
      onCreateFolder: async (args) => {
        await createFolder({
          resourceHubId: args.resourceHubId,
          folderId: args.folderId,
          name: args.name,
        });
      },
    },
  };

  return <ResourceHubPage {...props} />;
}
